import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { getSubscriptionStatus, getDaysRemaining } from '../utils/subscription';

const NAVY = '#1a2a4a';
const GOLD = '#b8860b';

// Simple SVG line chart for band score trend
function BandTrendChart({ scores }) {
  if (scores.length < 2) {
    return (
      <div style={{ height:120, display:'flex', alignItems:'center', justifyContent:'center', background:'#f8fafc', borderRadius:8, color:'#9aa3b8', fontSize:13 }}>
        {scores.length === 0 ? 'No essays submitted yet' : 'Submit at least 2 essays to see trend'}
      </div>
    );
  }
  const W = 320, H = 100, PAD = 12;
  const vals = scores.map(s => s.band_score || 0);
  const mn = Math.max(0, Math.min(...vals) - 0.5);
  const mx = Math.min(9, Math.max(...vals) + 0.5);
  const toX = i => PAD + (i / (vals.length - 1)) * (W - PAD * 2);
  const toY = v => PAD + ((mx - v) / (mx - mn || 1)) * (H - PAD * 2);
  const pts = vals.map((v,i) => `${toX(i)},${toY(v)}`).join(' ');

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', height:120, display:'block' }}>
      <defs>
        <linearGradient id="lineGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={GOLD} stopOpacity="0.3"/>
          <stop offset="100%" stopColor={GOLD} stopOpacity="0.02"/>
        </linearGradient>
      </defs>
      {/* Grid lines */}
      {[mn, (mn+mx)/2, mx].map((v,i) => (
        <g key={i}>
          <line x1={PAD} y1={toY(v)} x2={W-PAD} y2={toY(v)} stroke="#e8edf5" strokeWidth="1"/>
          <text x={0} y={toY(v)+4} fontSize={9} fill="#9aa3b8">{v.toFixed(1)}</text>
        </g>
      ))}
      {/* Area fill */}
      <polyline points={`${pts} ${toX(vals.length-1)},${H-PAD} ${PAD},${H-PAD}`} fill="url(#lineGrad)" stroke="none"/>
      {/* Line */}
      <polyline points={pts} fill="none" stroke={GOLD} strokeWidth="2" strokeLinejoin="round"/>
      {/* Dots */}
      {vals.map((v,i) => (
        <circle key={i} cx={toX(i)} cy={toY(v)} r={3} fill={GOLD} stroke="#fff" strokeWidth="1.5"/>
      ))}
    </svg>
  );
}

// Simple SVG radar/spider chart for skill breakdown
function RadarChart({ current, first }) {
  const skills = ['TA', 'CC', 'LR', 'GRA'];
  const fields = ['ta_score', 'cc_score', 'lr_score', 'gra_score'];
  const CX = 80, CY = 80, R = 58;
  const angles = skills.map((_, i) => (i * 2 * Math.PI / skills.length) - Math.PI / 2);
  const toPoint = (val, r) => {
    const angle = angles[r];
    const rad = (val / 9) * R;
    return { x: CX + rad * Math.cos(angle), y: CY + rad * Math.sin(angle) };
  };
  const polygon = (obj) => fields.map((f,i) => {
    const v = obj?.[f] || 0;
    const p = toPoint(v, i);
    return `${p.x},${p.y}`;
  }).join(' ');

  return (
    <svg viewBox="0 0 160 160" style={{ width:160, height:160 }}>
      {/* Grid circles */}
      {[3,6,9].map(v => (
        <polygon key={v} points={fields.map((_,i) => { const p = toPoint(v, i); return `${p.x},${p.y}`; }).join(' ')}
          fill="none" stroke="#e8edf5" strokeWidth="1"/>
      ))}
      {/* Axis lines */}
      {angles.map((a, i) => (
        <line key={i} x1={CX} y1={CY} x2={CX + R * Math.cos(a)} y2={CY + R * Math.sin(a)} stroke="#e8edf5" strokeWidth="1"/>
      ))}
      {/* First attempt (grey) */}
      {first && <polygon points={polygon(first)} fill="rgba(100,116,139,0.15)" stroke="#94a3b8" strokeWidth="1.5"/>}
      {/* Current (gold) */}
      {current && <polygon points={polygon(current)} fill={`${GOLD}33`} stroke={GOLD} strokeWidth="2"/>}
      {/* Labels */}
      {skills.map((s, i) => {
        const a = angles[i];
        const lx = CX + (R + 14) * Math.cos(a);
        const ly = CY + (R + 14) * Math.sin(a);
        return <text key={i} x={lx} y={ly + 4} textAnchor="middle" fontSize={9} fill={NAVY} fontWeight="600">{s}</text>;
      })}
    </svg>
  );
}

export default function ProgressDashboard({ student, studentId }) {
  const id = student?.id || studentId;
  const [scores, setScores] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    async function load() {
      setLoading(true);
      const [sc, bk] = await Promise.all([
        supabase.from('essay_scores').select('*').eq('student_id', id).order('submitted_at', { ascending:true }).limit(30),
        supabase.from('bookings').select('*').eq('student_id', id).eq('status', 'confirmed'),
      ]);
      setScores(sc.data || []);
      setBookings(bk.data || []);
      setLoading(false);
    }
    load();
  }, [id]);

  if (loading) return <div style={{ padding:40, textAlign:'center', color:'#9aa3b8' }}>Loading progress…</div>;

  const last10 = scores.slice(-10);
  const current = scores[scores.length - 1] || null;
  const first   = scores[0] || null;
  const best    = scores.reduce((b, s) => (!b || s.band_score > b.band_score) ? s : b, null);
  const hoursStudied = bookings.length * 1; // each booking = ~1h

  const subStatus = student ? getSubscriptionStatus(student) : null;
  const daysLeft  = student ? getDaysRemaining(student) : null;

  // Most improved skill
  let mostImproved = null;
  if (first && current) {
    const diffs = [
      { skill:'Task Achievement', diff:(current.ta_score||0)-(first.ta_score||0) },
      { skill:'Coherence & Cohesion', diff:(current.cc_score||0)-(first.cc_score||0) },
      { skill:'Lexical Resource', diff:(current.lr_score||0)-(first.lr_score||0) },
      { skill:'Grammar', diff:(current.gra_score||0)-(first.gra_score||0) },
    ];
    mostImproved = diffs.sort((a,b)=>b.diff-a.diff)[0];
  }

  const statBox = (label, value, sub, color='#1a2a4a') => (
    <div style={{ background:'#fff', border:'1px solid #e8edf5', borderRadius:12, padding:'16px 18px', textAlign:'center', boxShadow:'0 1px 6px rgba(26,42,74,0.06)' }}>
      <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:28, fontWeight:700, color }}>{value ?? '—'}</div>
      <div style={{ fontSize:11, color:'#5a6a88', marginTop:4, fontWeight:600 }}>{label}</div>
      {sub && <div style={{ fontSize:11, color:'#9aa3b8', marginTop:2 }}>{sub}</div>}
    </div>
  );

  return (
    <div style={{ padding:'0 0 24px' }}>
      <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:20, fontWeight:700, color:NAVY, marginBottom:18 }}>
        My Progress {student?.name ? `— ${student.name}` : ''}
      </div>

      {/* Stat cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))', gap:12, marginBottom:24 }}>
        {statBox('Essays Submitted', scores.length)}
        {statBox('Best Score', best?.band_score?.toFixed(1), 'Band Score', GOLD)}
        {statBox('Latest Score', current?.band_score?.toFixed(1) ?? '—', 'Band Score', current ? '#1a5fa8' : '#9aa3b8')}
        {statBox('Sessions', bookings.length, 'confirmed')}
        {statBox('Hours Studied', `${hoursStudied}h`)}
        {subStatus && statBox('Subscription', subStatus, daysLeft != null ? `${daysLeft} days left` : null,
          subStatus==='active'?'#27ae60':subStatus==='grace'?'#e67e22':'#e74c3c')}
      </div>

      {/* Band trend chart */}
      <div style={{ background:'#fff', border:'1px solid #e8edf5', borderRadius:14, padding:'20px', marginBottom:18, boxShadow:'0 1px 6px rgba(26,42,74,0.06)' }}>
        <div style={{ fontWeight:600, color:NAVY, marginBottom:12, fontSize:14 }}>Band Score Trend (last {last10.length} essays)</div>
        <BandTrendChart scores={last10}/>
        {last10.length > 0 && (
          <div style={{ display:'flex', justifyContent:'space-between', marginTop:6 }}>
            <span style={{ fontSize:11, color:'#9aa3b8' }}>Oldest</span>
            <span style={{ fontSize:11, color:'#9aa3b8' }}>Most Recent</span>
          </div>
        )}
      </div>

      {/* Radar + most improved */}
      <div style={{ display:'grid', gridTemplateColumns:'auto 1fr', gap:18, marginBottom:18 }}>
        <div style={{ background:'#fff', border:'1px solid #e8edf5', borderRadius:14, padding:'20px', boxShadow:'0 1px 6px rgba(26,42,74,0.06)' }}>
          <div style={{ fontWeight:600, color:NAVY, marginBottom:10, fontSize:14 }}>Skill Breakdown</div>
          <RadarChart current={current} first={first !== current ? first : null}/>
          <div style={{ display:'flex', gap:12, justifyContent:'center', marginTop:8 }}>
            <div style={{ display:'flex', alignItems:'center', gap:5 }}><div style={{ width:10, height:3, background:GOLD, borderRadius:2 }}/><span style={{ fontSize:10, color:'#5a6a88' }}>Current</span></div>
            {first && first !== current && <div style={{ display:'flex', alignItems:'center', gap:5 }}><div style={{ width:10, height:3, background:'#94a3b8', borderRadius:2 }}/><span style={{ fontSize:10, color:'#5a6a88' }}>First</span></div>}
          </div>
        </div>

        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          {mostImproved && mostImproved.diff > 0 && (
            <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:12, padding:'16px', boxShadow:'0 1px 6px rgba(26,42,74,0.04)' }}>
              <div style={{ fontSize:11, color:'#9aa3b8', fontWeight:600, marginBottom:6 }}>MOST IMPROVED</div>
              <div style={{ fontSize:18, fontWeight:700, color:'#16a34a', fontFamily:"'Cormorant Garamond',serif" }}>{mostImproved.skill}</div>
              <div style={{ fontSize:13, color:'#15803d' }}>+{mostImproved.diff.toFixed(1)} bands since first essay</div>
            </div>
          )}
          {current && (
            <div style={{ background:'#fff', border:'1px solid #e8edf5', borderRadius:12, padding:'16px', boxShadow:'0 1px 6px rgba(26,42,74,0.06)' }}>
              <div style={{ fontSize:11, color:'#9aa3b8', fontWeight:600, marginBottom:10 }}>LATEST SCORES</div>
              {[['TA','ta_score'],['CC','cc_score'],['LR','lr_score'],['GRA','gra_score']].map(([label, field]) => (
                <div key={field} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                  <span style={{ fontSize:12, color:'#5a6a88' }}>{label}</span>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <div style={{ width:80, height:5, background:'#f0f4f8', borderRadius:3, overflow:'hidden' }}>
                      <div style={{ height:'100%', width:`${((current[field]||0)/9)*100}%`, background:GOLD, borderRadius:3 }}/>
                    </div>
                    <span style={{ fontSize:12, fontWeight:700, color:NAVY, fontFamily:"'JetBrains Mono',monospace", width:24 }}>{current[field]?.toFixed(1) ?? '—'}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
